// Visualises the directed IR. Cues, pauses, emphasis, and register chips are
// distinct from the script text. Hovering a cue/pause/emphasis shows a
// capability tooltip telling the user how the chosen TTS provider will honour
// (or degrade) it.

import { useState } from 'react';
import type { Ir, Section, Block, Span } from '@scriptvoice/ir';
import type { TtsProvider } from '../api.js';
import { capabilityFor, type CapabilityLevel } from './capability-data.js';
import { Spinner } from './ui/spinner.js';

interface Props {
  ir: Ir;
  ttsProvider: TtsProvider;
  /** Called when the user edits a cue inline (no LLM call). */
  onChangeIr?: (ir: Ir) => void;
  /** Called when the user requests an LLM re-direct for a section. */
  onRegenSection?: (sectionId: string, tone: string) => void;
  /** Section currently being re-directed (disable interactions). */
  busySectionId?: string | null;
}

export function DirectedView({ ir, ttsProvider, onChangeIr, onRegenSection, busySectionId }: Props) {
  return (
    <article className="font-serif text-base leading-relaxed">
      <header className="mb-8 pb-6 border-b border-soft/60">
        <div className="flex items-center gap-2 flex-wrap text-xs uppercase tracking-wider text-muted/80 mb-3">
          <Chip>{ir.metadata.genre.replace(/_/g, ' ')}</Chip>
          <Chip>{ir.voice_profile.register}</Chip>
          <Chip>{ir.voice_profile.pace_default}</Chip>
          <Chip>{ir.voice_profile.energy_default}</Chip>
        </div>
        <p className="text-muted text-sm leading-relaxed italic">
          {ir.metadata.intent_summary}
        </p>
        {ir.voice_profile.arc_notes && (
          <p className="text-muted/80 text-xs mt-2 leading-relaxed">
            <span className="text-muted">Arc — </span>
            {ir.voice_profile.arc_notes}
          </p>
        )}
      </header>

      <div className="space-y-10">
        {ir.sections.map((section) => (
          <SectionView
            key={section.id}
            section={section}
            ir={ir}
            ttsProvider={ttsProvider}
            onChangeIr={onChangeIr}
            onRegenSection={onRegenSection}
            busy={busySectionId === section.id}
          />
        ))}
      </div>

      {ir.notes_for_user.length > 0 && (
        <aside className="mt-10 pt-6 border-t border-soft/60">
          <h3 className="text-xs uppercase tracking-wider text-muted mb-3">Director notes</h3>
          <ul className="space-y-2 text-sm text-muted/90 leading-relaxed">
            {ir.notes_for_user.map((note, i) => (
              <li key={i} className="pl-4 border-l-2 border-soft">
                {note}
              </li>
            ))}
          </ul>
        </aside>
      )}
    </article>
  );
}

const TONE_PRESETS = ['colder', 'warmer', 'drier', 'gentler', 'lighter', 'more restrained'] as const;

function SectionView({
  section,
  ir,
  ttsProvider,
  onChangeIr,
  onRegenSection,
  busy,
}: {
  section: Section;
  ir: Ir;
  ttsProvider: TtsProvider;
  onChangeIr?: (ir: Ir) => void;
  onRegenSection?: (sectionId: string, tone: string) => void;
  busy?: boolean;
}) {
  const register = section.register_override ?? ir.voice_profile.register;
  const pace = section.pace_override ?? ir.voice_profile.pace_default;
  const showOverride = section.register_override || section.pace_override;
  const isQuoted = section.kind === 'quoted_material';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <section className={`relative ${isQuoted ? 'pl-5 border-l-4 border-accent/30' : ''} ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-wider text-muted/70">
          <Chip subtle>{section.kind.replace(/_/g, ' ')}</Chip>
          <Chip subtle>{section.role}</Chip>
          {section.role_label && <span className="text-muted">{section.role_label}</span>}
          {showOverride && (
            <>
              <span className="text-muted/40">·</span>
              <Chip subtle>{register}</Chip>
              <Chip subtle>{pace}</Chip>
            </>
          )}
        </div>
        {onRegenSection && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              disabled={busy}
              className="text-muted/60 hover:text-ink text-xs leading-none px-2 py-1 rounded hover:bg-soft/60 disabled:opacity-60"
              title="Re-direct this section"
              aria-label="Re-direct this section"
              aria-expanded={menuOpen}
            >
              {busy ? <Spinner size={12} /> : '⋯'}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-soft rounded-md shadow-lg p-2 w-44 max-w-[calc(100vw-2rem)] font-sans animate-fade-in">
                <div className="text-[10px] uppercase tracking-wider text-muted mb-1.5 px-2">Re-direct as…</div>
                {TONE_PRESETS.map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onRegenSection(section.id, tone);
                    }}
                    className="block w-full text-left px-2 py-1.5 text-sm rounded hover:bg-soft/70 transition-colors"
                  >
                    {tone}
                  </button>
                ))}
                <div className="border-t border-soft/60 my-1" />
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  className="block w-full text-left px-2 py-1.5 text-xs text-muted hover:text-ink"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="space-y-4">
        {section.blocks.map((block) => (
          <BlockView
            key={block.id}
            block={block}
            ttsProvider={ttsProvider}
            section={section}
            ir={ir}
            onChangeIr={onChangeIr}
          />
        ))}
      </div>
    </section>
  );
}

function BlockView({
  block,
  ttsProvider,
  section,
  ir,
  onChangeIr,
}: {
  block: Block;
  ttsProvider: TtsProvider;
  section: Section;
  ir: Ir;
  onChangeIr?: (ir: Ir) => void;
}) {
  return (
    <p className="text-ink/95">
      {block.spans.map((span) => (
        <SpanView
          key={span.id}
          span={span}
          ttsProvider={ttsProvider}
          onChangeCueHint={
            onChangeIr && span.kind === 'cue'
              ? (newHint) => onChangeIr(updateCueHint(ir, section.id, block.id, span.id, newHint))
              : undefined
          }
        />
      ))}
    </p>
  );
}

const CUE_HINTS: Array<{ hint: CueHint; label: string }> = [
  { hint: 'hush', label: 'hush — near-whisper' },
  { hint: 'warm_lift', label: 'warm lift — slight smile' },
  { hint: 'dry_aside', label: 'dry aside — almost spoken aside' },
  { hint: 'concerned', label: 'concerned — careful' },
  { hint: 'tender', label: 'tender — intimate' },
  { hint: 'amused', label: 'amused — quiet smile' },
  { hint: 'grave', label: 'grave — restrained weight' },
  { hint: 'resolute', label: 'resolute — settled' },
  { hint: 'wry', label: 'wry — light irony' },
  { hint: 'urgent', label: 'urgent — clipped intensity' },
];

type CueHint =
  | 'hush' | 'warm_lift' | 'dry_aside' | 'concerned' | 'tender'
  | 'amused' | 'grave' | 'resolute' | 'wry' | 'urgent';

// Pure helper — returns a new IR with the given cue's hint replaced.
function updateCueHint(ir: Ir, sectionId: string, blockId: string, spanId: string, hint: CueHint): Ir {
  return {
    ...ir,
    sections: ir.sections.map((s) =>
      s.id !== sectionId
        ? s
        : {
            ...s,
            blocks: s.blocks.map((b) =>
              b.id !== blockId
                ? b
                : {
                    ...b,
                    spans: b.spans.map((sp) =>
                      sp.id !== spanId || sp.kind !== 'cue' ? sp : { ...sp, hint },
                    ),
                  },
            ),
          },
    ),
  };
}

function SpanView({
  span,
  ttsProvider,
  onChangeCueHint,
}: {
  span: Span;
  ttsProvider: TtsProvider;
  onChangeCueHint?: (hint: CueHint) => void;
}) {
  switch (span.kind) {
    case 'text':
      return <span>{span.text}</span>;
    case 'emphasis': {
      const cap = capabilityFor('emphasis', span.weight, ttsProvider);
      return (
        <CueTooltip
          label={`emphasis (${span.weight})`}
          cap={cap}
          provider={ttsProvider}
          desc={`The director marked this as ${span.weight} stress.`}
        >
          <span
            className={
              span.weight === 'strong'
                ? 'font-bold text-ink underline decoration-accent/40 decoration-2 underline-offset-4'
                : 'italic text-ink underline decoration-accent/30 decoration-1 underline-offset-4'
            }
          >
            {span.text}
          </span>
        </CueTooltip>
      );
    }
    case 'pause': {
      const cap = capabilityFor('pause', span.intent, ttsProvider);
      const glyph =
        span.intent === 'micro'
          ? '·'
          : span.intent === 'soft_trail'
            ? '…'
            : span.intent === 'clean_break'
              ? '—'
              : span.intent === 'full_beat'
                ? '∥'
                : '∥∥';
      return (
        <CueTooltip
          label={`pause: ${span.intent.replace(/_/g, ' ')}`}
          cap={cap}
          provider={ttsProvider}
          desc={pauseDesc(span.intent)}
        >
          <span className="inline-flex items-center mx-0.5 px-1.5 py-px rounded text-accent/80 bg-accent/5 text-xs font-mono align-baseline">
            {glyph}
          </span>
        </CueTooltip>
      );
    }
    case 'cue': {
      const cap = capabilityFor('cue', span.hint, ttsProvider);
      if (onChangeCueHint) {
        return <EditableCueChip span={span} ttsProvider={ttsProvider} cap={cap} onChange={onChangeCueHint} />;
      }
      return (
        <CueTooltip
          label={`cue: ${span.hint.replace(/_/g, ' ')}`}
          cap={cap}
          provider={ttsProvider}
          desc="A directing note to colour the next stretch of speech."
        >
          <span className="inline-flex items-center mr-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wide bg-accent/10 text-accent border border-accent/20 font-sans">
            {span.hint.replace(/_/g, ' ')}
          </span>
        </CueTooltip>
      );
    }
    case 'non_lexical': {
      const cap = capabilityFor('non_lexical', span.type, ttsProvider);
      return (
        <CueTooltip
          label={`sound: ${span.type.replace(/_/g, ' ')}`}
          cap={cap}
          provider={ttsProvider}
          desc="A non-lexical sound (breath, sigh, chuckle…). Most providers drop these."
        >
          <span className="inline-flex items-center mr-1 px-2 py-0.5 rounded-full text-[11px] bg-muted/10 text-muted border border-muted/20 italic font-sans">
            {span.type.replace(/_/g, ' ')}
          </span>
        </CueTooltip>
      );
    }
  }
}

function pauseDesc(intent: string): string {
  switch (intent) {
    case 'micro': return 'A breath between clauses.';
    case 'soft_trail': return 'A trailing thought, ellipsis-feeling.';
    case 'clean_break': return 'An em-dash break, sharp.';
    case 'full_beat': return 'A full pause for landing.';
    case 'hold_for_impact': return 'A longer, deliberate hold.';
    default: return '';
  }
}

function Chip({ children, subtle }: { children: React.ReactNode; subtle?: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] tracking-wider font-sans ${
        subtle ? 'bg-soft/60 text-muted' : 'bg-soft text-ink/80'
      }`}
    >
      {children}
    </span>
  );
}

function CueTooltip({
  children,
  label,
  cap,
  provider,
  desc,
}: {
  children: React.ReactNode;
  label: string;
  cap: CapabilityLevel;
  provider: TtsProvider;
  desc: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline cursor-help"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      {children}
      {open && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 z-20 pointer-events-none">
          <span className="block bg-ink text-parchment text-xs leading-relaxed rounded-lg shadow-lg p-3 font-sans">
            <span className="block font-medium tracking-wide mb-1">{label}</span>
            <span className="block text-parchment/80 mb-2">{desc}</span>
            <span className="flex items-center gap-2 text-[11px]">
              <CapabilityBadge cap={cap} />
              <span className="text-parchment/70">on {provider}</span>
            </span>
          </span>
        </span>
      )}
    </span>
  );
}

function CapabilityBadge({ cap }: { cap: CapabilityLevel }) {
  const styles = {
    full: 'bg-emerald-500/30 text-emerald-200',
    partial: 'bg-amber-500/30 text-amber-200',
    none: 'bg-rose-500/30 text-rose-200',
  } as const;
  const label = { full: 'honoured', partial: 'approximated', none: 'dropped' } as const;
  return (
    <span className={`inline-block px-2 py-0.5 rounded ${styles[cap]}`}>
      {label[cap]}
    </span>
  );
}

function EditableCueChip({
  span,
  ttsProvider,
  cap,
  onChange,
}: {
  span: { hint: string; id: string };
  ttsProvider: TtsProvider;
  cap: CapabilityLevel;
  onChange: (hint: CueHint) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block align-baseline mr-1">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wide bg-accent/10 text-accent border border-accent/20 hover:bg-accent/15 hover:border-accent/30 font-sans cursor-pointer"
        title={`Edit cue · ${cap} on ${ttsProvider}`}
      >
        {span.hint.replace(/_/g, ' ')}
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-20"
          />
          <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-soft rounded-md shadow-lg p-1 w-56 font-sans">
            <div className="text-[10px] uppercase tracking-wider text-muted mb-1 px-2 pt-1">Cue hint</div>
            {CUE_HINTS.map(({ hint, label }) => (
              <button
                key={hint}
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (hint !== span.hint) onChange(hint);
                }}
                className={`block w-full text-left px-2 py-1 text-sm rounded transition-colors ${
                  hint === span.hint ? 'bg-accent/10 text-accent' : 'hover:bg-soft/70'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}
